/**
 * The per-vehicle detail pop-down for the Teknisk tab (#472).
 *
 * Renders inside the results table as a full-width row beneath the vehicle it
 * belongs to, so the vehicle's identity stays on screen while its spec is
 * read. That is why it is not a modal: comparing two cars means opening two
 * panels, and a modal permits exactly one.
 */
import { useTranslation } from 'react-i18next'
import ExportButtons from '../../components/ExportButtons'
import type { Detaljer } from './oppslagApi'
import {
  GRUPPER,
  spesifikasjonKolonner,
  formater,
  mangler,
  tilSpesifikasjonsRader,
} from './detaljFelter'

interface Props {
  detaljer: Detaljer
  kjennemerke: string | null
  /** Used only to name the export file and title the PDF. */
  beskrivelse?: string
}

export default function KjoretoyDetaljer({ detaljer, kjennemerke, beskrivelse }: Props) {
  const { t } = useTranslation('oppslag')
  const rader = tilSpesifikasjonsRader(detaljer)

  return (
    <div className="space-y-4 p-4 bg-gray-50 dark:bg-gray-900/40 rounded-sm" data-testid="detaljer-panel">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
          {t('detaljer.heading', { plate: kjennemerke ?? t('detaljer.vehicleFallback') })}
        </h4>
        <div data-testid="detaljer-eksport">
        <ExportButtons
          data={rader}
          columns={spesifikasjonKolonner()}
          metadata={{
            title: t('detaljer.exportTitle', { plate: kjennemerke ?? '' }).trim(),
            filters: beskrivelse ? [beskrivelse] : undefined,
            explanationText: t('detaljer.exportExplanation'),
          }}
        />
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {GRUPPER.map((gruppe) => {
          const innhold = detaljer[gruppe.key] as Record<string, unknown> | undefined
          if (!innhold) return null
          // A group SVV had nothing for — common for miljø on older records —
          // is dropped entirely rather than rendered as a column of dashes.
          const felter = gruppe.felter.filter((f) => !mangler(innhold[f.key]))
          if (!felter.length) return null

          return (
            <div key={gruppe.key} data-testid={`detaljer-gruppe-${gruppe.key}`}>
              <h5 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
                {t(gruppe.tittelKey)}
              </h5>
              <dl className="text-sm divide-y divide-gray-200 dark:divide-gray-700">
                {felter.map((felt) => (
                  <div key={felt.key} className="flex justify-between gap-3 py-1">
                    <dt className="text-gray-500 dark:text-gray-400">{t(felt.labelKey)}</dt>
                    <dd className="text-gray-900 dark:text-gray-100 text-right">
                      {formater(innhold[felt.key], felt.enhet)}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          )
        })}
      </div>

      {!!detaljer.dekk_og_felg?.length && (
        <div data-testid="detaljer-dekk">
          <h5 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
            {t('detaljer.dekkOgFelg')}
          </h5>
          <div className="overflow-x-auto">
            <table className="data-table data-table-compact">
              <thead>
                <tr>
                  <th>{t('detaljer.dekk.kombinasjon')}</th>
                  <th>{t('detaljer.dekk.aksel')}</th>
                  <th>{t('detaljer.dekk.dekk')}</th>
                  <th>{t('detaljer.dekk.felg')}</th>
                  <th>{t('detaljer.dekk.belastning')}</th>
                  <th>{t('detaljer.dekk.hastighet')}</th>
                  <th>{t('detaljer.dekk.innpress')}</th>
                </tr>
              </thead>
              <tbody>
                {detaljer.dekk_og_felg.map((d, i) => (
                  <tr key={`${d.kombinasjon}-${d.aksel}-${i}`}>
                    <td>{d.kombinasjon}</td>
                    <td>{d.aksel ?? '—'}</td>
                    <td>{d.dekkdimensjon ?? '—'}</td>
                    <td>{d.felgdimensjon ?? '—'}</td>
                    <td>{d.belastningskode ?? '—'}</td>
                    <td>{d.hastighetskode ?? '—'}</td>
                    <td>{d.innpress ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!!detaljer.merknader?.length && (
        <div data-testid="detaljer-merknader">
          <h5 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
            {t('detaljer.merknader')}
          </h5>
          <ul className="text-sm text-gray-700 dark:text-gray-300 list-disc pl-5 space-y-1">
            {detaljer.merknader.map((m, i) => (
              <li key={i}>{m}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
